import { SpanStatusCode } from '@opentelemetry/api';
import type { ReadableSpan, Span, SpanProcessor } from '@opentelemetry/sdk-trace-base';

/**
 * A 404 is an expected answer, not a client failure.
 *
 * The XHR and fetch instrumentations mark every response `>= 400` as ERROR, so each profile the
 * API genuinely does not have (a full-view company or person that isn't in our data) surfaced as an
 * error span on the web — alongside the real ones. Only 404 is downgraded: 400/401/403 still mean
 * the client sent something wrong or wasn't allowed, and 5xx and network failures are faults.
 * The server applies the same rule in `responseStatusProcessor.ts`.
 *
 * Registered before Logfire's exporting processor, so mutating in `onEnd` is visible to the exporter.
 */
export class NotFoundIsNotAnErrorSpanProcessor implements SpanProcessor {
  onStart(_span: Span): void {}

  onEnd(span: ReadableSpan): void {
    try {
      const status = span.attributes['http.response.status_code'] ?? span.attributes['http.status_code'];
      if (Number(status) !== 404 || span.status.code !== SpanStatusCode.ERROR) return;

      const mutable = span as unknown as {
        attributes: Record<string, unknown>;
        status: { code: SpanStatusCode; message?: string };
      };

      mutable.status = { code: SpanStatusCode.UNSET };
      delete mutable.attributes['error.type'];
    } catch {
      // A reporting fix must never throw into the exporter.
    }
  }

  async forceFlush(): Promise<void> {}

  async shutdown(): Promise<void> {}
}
